<template>
    <div id="app">
        <!-- Events Breakdown Popup -->
        <div v-if="showEventsBreakdown" class="simple-popup" @click.self="showEventsBreakdown = false">
            <div class="popup-content">
                <div class="popup-header">
                    <h3>Events Breakdown - {{ eventsBreakdownTitle }}</h3>
                    <button @click="showEventsBreakdown = false" class="close-btn">×</button>
                </div>
                <div class="popup-body">
                    <div class="events-summary">
                        <div class="summary-item">
                            <span class="summary-label">Total Events:</span>
                            <span class="summary-value">{{ totalEventsInRange }}</span>
                        </div>
                        <div class="summary-item">
                            <span class="summary-label">Time Period:</span>
                            <span class="summary-value">{{ currentPeriodLabel }}</span>
                        </div>
                        <div class="summary-item">
                            <span class="summary-label">Active Devices:</span>
                            <span class="summary-value">{{ eventsByDevice.length }}</span>
                        </div>
                    </div>

                    <div class="events-list-container">
                        <h4>Events by FAMS Device</h4>
                        <div v-for="device in eventsByDevice" :key="device.deviceId" class="events-device-item">
                            <div class="device-header" @click="device.expanded = !device.expanded">
                                <div class="device-info-row">
                                    <div class="device-name-section">
                                        <strong>{{ device.deviceId }}</strong>
                                        <span class="device-alias">{{ device.alias || 'No alias' }}</span>
                                    </div>
                                    <div class="device-count">
                                        <span class="event-count-badge">{{ device.totalEvents }} events</span>
                                        <span class="expand-icon">{{ device.expanded ? '▼' : '▶' }}</span>
                                    </div>
                                </div>
                                <div class="device-progress-bar">
                                    <div class="progress-fill" :style="{ width: device.percentage + '%' }"></div>
                                </div>
                            </div>

                            <div v-if="device.expanded" class="device-events-detail">
                                <div class="event-type-grid">
                                    <div v-for="eventType in device.eventTypes" :key="eventType.type"
                                        class="event-type-item">
                                        <span :class="['activity-badge', 'badge-' + eventType.type]">
                                            {{ eventType.type }}
                                        </span>
                                        <span class="event-count">{{ eventType.count }}</span>
                                    </div>
                                </div>
                                <div class="device-timeline">
                                    <div class="timeline-item">
                                        <span class="timeline-label">First Event:</span>
                                        <span class="timeline-value">{{ formatDateTime(device.firstEvent) }}</span>
                                    </div>
                                    <div class="timeline-item">
                                        <span class="timeline-label">Last Event:</span>
                                        <span class="timeline-value">{{ formatDateTime(device.lastEvent) }}</span>
                                    </div>
                                    <div class="timeline-item">
                                        <span class="timeline-label">Peak Hour:</span>
                                        <span class="timeline-value">{{ device.peakHour }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="eventsByDevice.length === 0" class="no-events">
                            No events found in the selected time range.
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Volume Breakdown Popup -->
        <div v-if="showVolumeBreakdown" class="simple-popup" @click.self="showVolumeBreakdown = false">
            <div class="popup-content">
                <div class="popup-header">
                    <h3>Volume Breakdown - {{ (stats.totalVolume || 0).toFixed(2) }}L Total</h3>
                    <button @click="showVolumeBreakdown = false" class="close-btn">×</button>
                </div>
                <div class="popup-body">
                    <div class="volume-summary">
                        <div class="summary-item">
                            <span class="summary-label">Total Dispensed:</span>
                            <span class="summary-value">{{ (stats.totalVolume || 0).toFixed(2) }}L</span>
                        </div>
                        <div class="summary-item">
                            <span class="summary-label">Average per Event:</span>
                            <span class="summary-value">{{ (stats.avgVolume || 0).toFixed(2) }}L</span>
                        </div>
                        <div class="summary-item">
                            <span class="summary-label">Events with Volume:</span>
                            <span class="summary-value">{{ volumeEventCount }}</span>
                        </div>
                    </div>

                    <div class="volume-list-container">
                        <h4>Volume by Device</h4>
                        <div v-for="device in volumeByDevice" :key="device.deviceId" class="volume-item">
                            <div class="volume-device-info">
                                <div class="device-name">
                                    <strong>{{ device.deviceId }}</strong>
                                    <span class="device-alias">{{ device.alias || '' }}</span>
                                </div>
                                <div class="volume-amount">
                                    <span class="volume-value">{{ device.totalVolume.toFixed(2) }}L</span>
                                    <span class="volume-percentage">({{ device.percentage.toFixed(1) }}%)</span>
                                </div>
                            </div>
                            <div class="volume-bar-container">
                                <div class="volume-bar" :style="{ width: device.percentage + '%' }"></div>
                            </div>
                            <div class="volume-details">
                                <span class="detail-badge">{{ device.eventCount }} events</span>
                                <span class="detail-badge">Avg: {{ device.avgVolume.toFixed(2) }}L</span>
                                <span class="detail-badge">Max: {{ device.maxVolume.toFixed(2) }}L</span>
                            </div>
                        </div>

                        <div v-if="volumeByDevice.length === 0" class="no-volume">
                            No volume data found in the selected time range.
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Device List Popup -->
        <div v-if="showDeviceList" class="simple-popup" @click.self="showDeviceList = false">
            <div class="popup-content">
                <div class="popup-header">
                    <h3>Active Devices ({{ uniqueDevices.length }})</h3>
                    <button @click="showDeviceList = false" class="close-btn">×</button>
                </div>
                <div class="popup-body">
                    <div class="device-list-container">
                        <div v-for="device in deviceListWithStats" :key="device.id" class="device-list-item">
                            <div class="device-main-info">
                                <strong>{{ device.id }}</strong>
                                <span class="device-alias">{{ device.alias || 'No alias' }}</span>
                            </div>
                            <div class="device-stats">
                                <span class="device-stat-badge">{{ device.eventCount }} events</span>
                                <span class="device-stat-badge volume">{{ device.totalVolume.toFixed(2) }}L</span>
                                <span class="device-stat-badge" :class="'status-' + device.lastStatus">{{
                                    device.lastStatus }}</span>
                            </div>
                        </div>
                        <div v-if="uniqueDevices.length === 0" class="no-devices">
                            No active devices found in the selected time range.
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Activity Details Popup -->
        <div v-if="selectedActivity" class="simple-popup" @click.self="selectedActivity = null">
            <div class="popup-content">
                <div class="popup-header">
                    <h3>Activity Details</h3>
                    <button @click="selectedActivity = null" class="close-btn">×</button>
                </div>
                <div class="popup-body">
                    <div class="detail-row">
                        <strong>Date:</strong> {{ formatDateTime(selectedActivity.timestamp ||
                        selectedActivity.createDate) }}
                    </div>
                    <div class="detail-row">
                        <strong>Device ID:</strong> {{ selectedActivity.deviceId }}
                    </div>
                    <div class="detail-row">
                        <strong>Device Name:</strong> {{ selectedActivity.deviceName || '-' }}
                    </div>
                    <div class="detail-row">
                        <strong>Store:</strong> {{ selectedActivity.storeName || '-' }}
                    </div>
                    <div class="detail-row">
                        <strong>Activity Type:</strong>
                        <span :class="['activity-badge', 'badge-' + selectedActivity.type]">{{ selectedActivity.type
                            }}</span>
                    </div>
                    <div class="detail-row">
                        <strong>ATG Event Type:</strong> {{ selectedActivity.atgEventType || '-' }}
                    </div>
                    <div class="detail-row">
                        <strong>Telemetry Type:</strong> {{ selectedActivity.telemetryType != null ?
                            selectedActivity.telemetryType : '-' }}
                    </div>
                    <div class="detail-row">
                        <strong>Status:</strong>
                        <span :class="'status-' + selectedActivity.status">{{ selectedActivity.status }}</span>
                    </div>
                    <div class="detail-row">
                        <strong>Message:</strong><br>
                        <div class="message-text">{{ selectedActivity.message }}</div>
                    </div>
                    <div class="detail-row">
                        <strong>Telemetry Data:</strong><br>
                        <div v-if="selectedActivity.tanks && selectedActivity.tanks.length > 0" class="tank-table-wrapper">
                            <table class="tank-detail-table">
                                <thead>
                                    <tr>
                                        <th>Tank</th>
                                        <th>Vol (L)</th>
                                        <th>Vol TC (L)</th>
                                        <th>Level (mm)</th>
                                        <th>Temp (°C)</th>
                                        <th>Water (L)</th>
                                        <th>State</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="tank in selectedActivity.tanks" :key="tank.id">
                                        <td><strong>{{ tank.name }}</strong></td>
                                        <td class="vol-cell">{{ tank.vol.toFixed(2) }}</td>
                                        <td class="vol-cell">{{ tank.volTC.toFixed(2) }}</td>
                                        <td>{{ tank.mm.toFixed(2) }}</td>
                                        <td>{{ tank.temp.toFixed(1) }}</td>
                                        <td>{{ tank.waterVol.toFixed(2) }}</td>
                                        <td><span :class="['state-badge', tank.stateInfo === 'Done' ? 'state-done' : 'state-other']">{{ tank.stateInfo || '-' }}</span></td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr class="tank-total-row">
                                        <td><strong>Total</strong></td>
                                        <td class="vol-cell"><strong>{{ selectedActivity.tanks.reduce((s,t) => s + t.vol, 0).toFixed(2) }}</strong></td>
                                        <td class="vol-cell"><strong>{{ selectedActivity.tanks.reduce((s,t) => s + t.volTC, 0).toFixed(2) }}</strong></td>
                                        <td colspan="4"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        <div v-else class="message-text">{{ selectedActivity.telemetryData || '-' }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Header -->
        <div class="header-toolbar">
            <div class="header-content">
                <div class="logo-section">
                    <div class="logo-icon">
                        <span class="icon-pulse">⚡</span>
                    </div>
                    <h1 class="dashboard-title">FAMS ATG Data Dashboard</h1>
                </div>
                <div class="header-controls">
                    <div class="loader-controls">
                        <div class="loader-toggle">
                            <label class="toggle-label">
                                <input type="checkbox" v-model="showLoaderOption" class="toggle-checkbox">
                                <span class="toggle-text">Show Loader</span>
                            </label>
                        </div>
                        <div v-if="showLoaderOption" class="loader-speed">
                            <label class="speed-label">
                                <span class="speed-text">Speed:</span>
                                <select v-model="minLoaderTime" class="speed-select">
                                    <option :value="0">Instant</option>
                                    <option :value="500">Fast (0.5s)</option>
                                    <option :value="1000">Normal (1s)</option>
                                    <option :value="1500">Smooth (1.5s)</option>
                                    <option :value="2000">Slow (2s)</option>
                                    <option :value="3000">Very Slow (3s)</option>
                                </select>
                            </label>
                        </div>
                        <div class="auto-refresh-toggle">
                            <label class="toggle-label">
                                <input type="checkbox" v-model="autoRefreshEnabled" @change="handleAutoRefreshToggle"
                                    class="toggle-checkbox">
                                <span class="toggle-text">Auto Refresh</span>
                            </label>
                        </div>
                        <div v-if="autoRefreshEnabled" class="refresh-interval">
                            <label class="interval-label">
                                <span class="interval-text">Every:</span>
                                <select v-model="refreshInterval" @change="handleRefreshIntervalChange"
                                    class="interval-select">
                                    <option :value="15000">15 sec</option>
                                    <option :value="30000">30 sec</option>
                                    <option :value="60000">1 min</option>
                                    <option :value="300000">5 min</option>
                                    <option :value="600000">10 min</option>
                                    <option :value="1800000">30 min</option>
                                </select>
                            </label>
                        </div>
                    </div>
                    <div class="live-indicator">
                        <div :class="['live-dot', { 'error': errorCount >= 3, 'auto-refresh': autoRefreshEnabled }]">
                        </div>
                        <span style="color: white; font-size: 0.875rem;">
                            {{ autoRefreshEnabled ? `Live (${formatRefreshInterval()})` : connectionStatus }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Global Loader Overlay -->
        <div v-if="showLoaderOption && isLoading" class="global-loader-overlay">
            <div class="global-loader-content">
                <div class="global-loader"></div>
                <div class="loader-text">{{ loaderMessage }}</div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">
            <!-- Statistics Section -->
            <div class="stats-section">
                <div class="stats-header">
                    <h3 class="stats-title">Statistics Overview</h3>
                    <button class="toggle-stats-btn" @click="statsVisible = !statsVisible">
                        <span class="toggle-icon">{{ statsVisible ? '📊' : '📈' }}</span>
                        {{ statsVisible ? 'Hide Statistics' : 'Show Statistics' }}
                    </button>
                </div>

                <transition name="slide-fade">
                    <div v-if="statsVisible" class="stats-wrapper">
                        <div class="stats-container">
                            <div class="stat-card clickable-stat" @click="openEventsBreakdown"
                                title="Click to see events breakdown">
                                <div class="stat-label">
                                    {{ getStatsDateRangeLabel() }}
                                </div>
                                <div class="stat-value">{{ stats.totalActivities || 0 }}</div>
                                <div class="stat-change"
                                    :class="(stats.eventsTrend || 0) > 0 ? 'positive' : 'negative'">
                                    <span class="stat-icon">{{ (stats.eventsTrend || 0) > 0 ? '📈' : '📉' }}</span>
                                    <span>
                                        {{ (filters.startDateTime || filters.endDateTime)
                                            ? Math.abs(stats.eventsTrend || 0).toFixed(1) + '% change in period'
                                            : Math.abs(stats.eventsTrend || 0).toFixed(1) + '% from yesterday'
                                        }}
                                    </span>
                                </div>
                            </div>

                            <div class="stat-card clickable-stat" @click="openDeviceList"
                                title="Click to see device list">
                                <div class="stat-label">Active Devices</div>
                                <div class="stat-value">{{ stats.totalDevices || 0 }}</div>
                                <div class="stat-change positive">
                                    <span class="stat-icon">✓</span>
                                    <span>All systems operational</span>
                                </div>
                            </div>

                            <div class="stat-card clickable-stat" @click="openVolumeBreakdown"
                                title="Click to see volume breakdown">
                                <div class="stat-label">Total Volume (L)</div>
                                <div class="stat-value">{{ (stats.totalVolume || 0).toFixed(2) }}</div>
                                <div class="stat-change positive">
                                    <span class="stat-icon">💧</span>
                                    <span>{{ (stats.avgVolume || 0).toFixed(1) }}L average</span>
                                </div>
                            </div>

                            <div class="stat-card">
                                <div class="stat-label">Processing Rate</div>
                                <div class="stat-value">{{ (stats.processingRate || 0).toFixed(1) }}%</div>
                                <div class="stat-change"
                                    :class="(stats.processingRate || 0) >= 95 ? 'positive' : 'negative'">
                                    <span class="stat-icon">⚡</span>
                                    <span>{{ stats.unprocessedCount || 0 }} pending</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Search / Filter Section -->
            <div class="search-section">
                <div class="filter-header">
                    <h3 class="filter-title">Filters</h3>
                    <button class="toggle-filter-btn" @click="filtersVisible = !filtersVisible">
                        <span class="toggle-icon">{{ filtersVisible ? '📁' : '📂' }}</span>
                        {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
                    </button>
                </div>

                <transition name="slide-fade">
                    <div v-if="filtersVisible" class="search-controls-wrapper">
                        <div class="search-controls">
                            <div class="search-group date-filter-group">
                                <label class="search-label">Date Range <span class="date-range-showing">{{
                                        activeDateRangeLabel }}</span></label>
                                <div class="date-quick-buttons">
                                    <button v-for="opt in dateRangeOptions" :key="opt.value"
                                        :class="['date-quick-btn', { active: selectedDateRange === opt.value }]"
                                        @click="selectDateRange(opt.value)">{{ opt.label }}</button>
                                </div>
                            </div>

                            <div class="search-group">
                                <label class="search-label">Activity Type <span class="multi-badge"
                                        v-if="filters.activityTypes.length">({{ filters.activityTypes.length
                                        }})</span></label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('activityType')">
                                        <span v-if="filters.activityTypes.length === 0" class="placeholder">All
                                            Activities</span>
                                        <span v-else class="selected-items">{{ filters.activityTypes.length }}
                                            selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.activityType" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllActivityTypes"
                                                    :checked="filters.activityTypes.length === 0">
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="type in activityTypeOptions"
                                                :key="type.value">
                                                <input type="checkbox" :value="type.value"
                                                    v-model="filters.activityTypes" @change="applyFilters">
                                                <span>{{ type.label }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="search-group">
                                <label class="search-label">Store <span class="multi-badge"
                                        v-if="filters.deviceIds.length">({{ filters.deviceIds.length }})</span></label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('device')">
                                        <span v-if="filters.deviceIds.length === 0" class="placeholder">All
                                            Devices</span>
                                        <span v-else class="selected-items">{{ filters.deviceIds.length }}
                                            selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.device" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllDevices"
                                                    :checked="filters.deviceIds.length === 0">
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="device in uniqueDevices"
                                                :key="device">
                                                <input type="checkbox" :value="device" v-model="filters.deviceIds"
                                                    @change="applyFilters">
                                                <span>{{ device }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="search-group">
                                <label class="search-label">Status <span class="multi-badge"
                                        v-if="filters.statuses.length">({{ filters.statuses.length }})</span></label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('status')">
                                        <span v-if="filters.statuses.length === 0" class="placeholder">All Status</span>
                                        <span v-else class="selected-items">{{ filters.statuses.length }}
                                            selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.status" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllStatuses"
                                                    :checked="filters.statuses.length === 0">
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="status in statusOptions"
                                                :key="status.value">
                                                <input type="checkbox" :value="status.value" v-model="filters.statuses"
                                                    @change="applyFilters">
                                                <span>{{ status.label }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Column Selector -->
                            <div class="search-group">
                                <label class="search-label">
                                    Visible Columns
                                    <span class="multi-badge" v-if="selectedColumns.length !== availableColumns.length">
                                        ({{ selectedColumns.length }}/{{ availableColumns.length }})
                                    </span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('columns')">
                                        <span v-if="selectedColumns.length === availableColumns.length"
                                            class="placeholder">All Columns</span>
                                        <span v-else class="selected-items">{{ selectedColumns.length }} columns
                                            selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.columns" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllColumns"
                                                    :checked="selectedColumns.length === availableColumns.length">
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="column in availableColumns"
                                                :key="column.key">
                                                <input type="checkbox" :value="column.key" v-model="selectedColumns"
                                                    @change="handleColumnVisibilityChange">
                                                <span>{{ column.label }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="filter-actions">
                            <button class="apply-filters-btn" @click="applyFilters">Apply Filters</button>
                            <button class="reset-filters-btn" @click="resetFilters">Reset</button>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Data Grid -->
            <div class="grid-container">
                <div class="grid-header">
                    <div class="grid-title">IOT ATG Data Feed</div>
                    <div class="grid-controls">
                        <button v-if="hasActiveColumnSearches" class="clear-column-searches-btn"
                            @click="clearColumnSearches">
                            <span class="action-icon">✖</span><span>Clear Searches</span>
                        </button>
                        <div class="rows-selector">
                            <span class="rows-label">Show:</span>
                            <select v-model="itemsPerPage" @change="handleRowsChange" class="rows-select">
                                <option :value="10">10 rows</option>
                                <option :value="15">15 rows</option>
                                <option :value="20">20 rows</option>
                                <option :value="25">25 rows</option>
                                <option :value="50">50 rows</option>
                                <option :value="100">100 rows</option>
                            </select>
                        </div>
                        <div class="grid-actions">
                            <button class="action-button popout-btn" @click="openInNewWindow" title="Pop Out">
                                <span class="btn-icon">⬜</span>
                                <span>Pop Out</span>
                            </button>
                            <button class="action-button refresh-btn" @click="refreshData" title="Refresh">
                                <span class="btn-icon">🔄</span>
                                <span>Refresh</span>
                            </button>
                            <button class="action-button export-btn" @click="exportData" title="Export">
                                <span class="btn-icon">📥</span>
                                <span>Export</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="table-container">
                    <!-- Loading State -->
                    <div v-if="loading" class="loading-overlay">
                        <div class="loader"></div>
                    </div>

                    <!-- Data Table -->
                    <table v-if="!loading && filteredActivities.length > 0" class="data-table">
                        <thead>
                            <tr>
                                <th v-for="column in visibleColumns" :key="column.key">{{ column.label }}</th>
                            </tr>
                            <tr class="search-row">
                                <th v-for="column in visibleColumns" :key="'s-' + column.key" class="search-cell">
                                    <input type="text" class="column-search-input" placeholder="Search..."
                                        v-model="columnSearchFilters[column.key]" @input="handleColumnSearch" />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="activity in paginatedActivities">

                                <!-- ── Activities WITH tank data: one <tr> per tank ── -->
                                <template v-if="activity.tanks && activity.tanks.length > 0">
                                    <tr v-for="(tank, tankIndex) in activity.tanks"
                                        :key="activity.id + '-tank-' + tank.id"
                                        @click="showActivityDetails(activity)"
                                        :class="['tank-data-row', tankIndex === 0 ? 'tank-first-row' : 'tank-subsequent-row', tankIndex === activity.tanks.length - 1 ? 'tank-last-row' : '']"
                                        style="cursor: pointer;">

                                        <!-- Shared columns — only on FIRST tank row, spanning all tank rows -->
                                        <td v-if="isColumnVisible('timestamp') && tankIndex === 0"
                                            :rowspan="activity.tanks.length" class="shared-cell">
                                            <div>{{ formatDateTime(activity.timestamp || activity.createDate) }}</div>
                                            <div style="font-size: 0.75rem; color: #6c757d;">
                                                {{ getRelativeTime(activity.timestamp || activity.createDate) }}
                                            </div>
                                        </td>
                                        <td v-if="isColumnVisible('deviceId') && tankIndex === 0"
                                            :rowspan="activity.tanks.length" class="shared-cell">
                                            <div class="device-info">
                                                <span class="device-id">{{ activity.deviceId }}</span>
                                                <span class="device-alias">{{ activity.deviceName }}</span>
                                            </div>
                                        </td>
                                        <td v-if="isColumnVisible('storeName') && tankIndex === 0"
                                            :rowspan="activity.tanks.length" class="shared-cell">
                                            {{ activity.storeName || '-' }}
                                        </td>
                                        <td v-if="isColumnVisible('type') && tankIndex === 0"
                                            :rowspan="activity.tanks.length" class="shared-cell">
                                            {{ activity.atgEventType || '-' }}
                                        </td>
                                        <td v-if="isColumnVisible('telemetryType') && tankIndex === 0"
                                            :rowspan="activity.tanks.length" class="shared-cell">
                                            {{ activity.telemetryType != null ? activity.telemetryType : '-' }}
                                        </td>
                                        <td v-if="isColumnVisible('message') && tankIndex === 0"
                                            :rowspan="activity.tanks.length" class="shared-cell">
                                            <div class="message-cell">{{ activity.message }}</div>
                                        </td>
                                        <td v-if="isColumnVisible('telemetryData') && tankIndex === 0"
                                            :rowspan="activity.tanks.length" class="shared-cell">
                                            <div class="message-cell">{{ activity.telemetryData || '-' }}</div>
                                        </td>

                                        <!-- Tank Volumes column — unique per row -->
                                        <td v-if="isColumnVisible('tankVolumes')" class="tank-cell"
                                            :class="tankIndex === activity.tanks.length - 1 ? 'tank-cell-last' : ''">
                                            <div class="tank-row-display">
                                                <span class="tank-number-badge">{{ tank.name }}</span>
                                                <span class="tank-vol-amount"> {{ tank.vol.toFixed(2) }} L</span>
                                            </div>
                                        </td>

                                        <!-- Status — shared -->
                                        <td v-if="isColumnVisible('status') && tankIndex === 0"
                                            :rowspan="activity.tanks.length" class="shared-cell">
                                            <div class="status-indicator" :class="'status-' + activity.status">
                                                <span class="status-dot"></span>
                                                {{ activity.status }}
                                            </div>
                                        </td>

                                        <!-- Additional columns — shared -->
                                        <td v-if="isColumnVisible('storeID') && tankIndex === 0" :rowspan="activity.tanks.length" class="shared-cell">{{ activity.storeID || '-' }}</td>
                                        <td v-if="isColumnVisible('accountID') && tankIndex === 0" :rowspan="activity.tanks.length" class="shared-cell">{{ activity.accountID || '-' }}</td>
                                        <td v-if="isColumnVisible('accountName') && tankIndex === 0" :rowspan="activity.tanks.length" class="shared-cell">{{ activity.accountName || '-' }}</td>
                                        <td v-if="isColumnVisible('recordTypeID') && tankIndex === 0" :rowspan="activity.tanks.length" class="shared-cell">{{ activity.recordTypeID || '-' }}</td>
                                        <td v-if="isColumnVisible('iotDeviceID') && tankIndex === 0" :rowspan="activity.tanks.length" class="shared-cell">{{ activity.iotDeviceID || '-' }}</td>
                                        <td v-if="isColumnVisible('iotDeviceDescription') && tankIndex === 0" :rowspan="activity.tanks.length" class="shared-cell">{{ activity.iotDeviceDescription || '-' }}</td>
                                        <td v-if="isColumnVisible('iotDeviceTypeID') && tankIndex === 0" :rowspan="activity.tanks.length" class="shared-cell">{{ activity.iotDeviceTypeID || '-' }}</td>
                                        <td v-if="isColumnVisible('iotDeviceTankID') && tankIndex === 0" :rowspan="activity.tanks.length" class="shared-cell">{{ activity.iotDeviceTankID != null ? activity.iotDeviceTankID : '-' }}</td>
                                        <td v-if="isColumnVisible('storeMacAddress') && tankIndex === 0" :rowspan="activity.tanks.length" class="shared-cell">{{ activity.storeMacAddress || '-' }}</td>
                                        <td v-if="isColumnVisible('storeIPAddress') && tankIndex === 0" :rowspan="activity.tanks.length" class="shared-cell">{{ activity.storeIPAddress || '-' }}</td>
                                    </tr>
                                </template>

                                <!-- ── Activities WITHOUT tank data: single row ── -->
                                <tr v-else
                                    :key="activity.id + '-no-tanks'"
                                    @click="showActivityDetails(activity)"
                                    style="cursor: pointer;">
                                    <td v-if="isColumnVisible('timestamp')">
                                        <div>{{ formatDateTime(activity.timestamp || activity.createDate) }}</div>
                                        <div style="font-size: 0.75rem; color: #6c757d;">
                                            {{ getRelativeTime(activity.timestamp || activity.createDate) }}
                                        </div>
                                    </td>
                                    <td v-if="isColumnVisible('deviceId')">
                                        <div class="device-info">
                                            <span class="device-id">{{ activity.deviceId }}</span>
                                            <span class="device-alias">{{ activity.deviceName }}</span>
                                        </div>
                                    </td>
                                    <td v-if="isColumnVisible('storeName')">{{ activity.storeName || '-' }}</td>
                                    <td v-if="isColumnVisible('type')">{{ activity.atgEventType || '-' }}</td>
                                    <td v-if="isColumnVisible('telemetryType')">{{ activity.telemetryType != null ? activity.telemetryType : '-' }}</td>
                                    <td v-if="isColumnVisible('message')">
                                        <div class="message-cell">{{ activity.message }}</div>
                                    </td>
                                    <td v-if="isColumnVisible('telemetryData')">
                                        <div class="message-cell">{{ activity.telemetryData || '-' }}</div>
                                    </td>
                                    <td v-if="isColumnVisible('tankVolumes')"><span>-</span></td>
                                    <td v-if="isColumnVisible('status')">
                                        <div class="status-indicator" :class="'status-' + activity.status">
                                            <span class="status-dot"></span>
                                            {{ activity.status }}
                                        </div>
                                    </td>
                                    <td v-if="isColumnVisible('storeID')">{{ activity.storeID || '-' }}</td>
                                    <td v-if="isColumnVisible('accountID')">{{ activity.accountID || '-' }}</td>
                                    <td v-if="isColumnVisible('accountName')">{{ activity.accountName || '-' }}</td>
                                    <td v-if="isColumnVisible('recordTypeID')">{{ activity.recordTypeID || '-' }}</td>
                                    <td v-if="isColumnVisible('iotDeviceID')">{{ activity.iotDeviceID || '-' }}</td>
                                    <td v-if="isColumnVisible('iotDeviceDescription')">{{ activity.iotDeviceDescription || '-' }}</td>
                                    <td v-if="isColumnVisible('iotDeviceTypeID')">{{ activity.iotDeviceTypeID || '-' }}</td>
                                    <td v-if="isColumnVisible('iotDeviceTankID')">{{ activity.iotDeviceTankID != null ? activity.iotDeviceTankID : '-' }}</td>
                                    <td v-if="isColumnVisible('storeMacAddress')">{{ activity.storeMacAddress || '-' }}</td>
                                    <td v-if="isColumnVisible('storeIPAddress')">{{ activity.storeIPAddress || '-' }}</td>
                                </tr>

                            </template>
                        </tbody>
                    </table>

                    <!-- Empty State -->
                    <div v-if="!loading && filteredActivities.length === 0" class="empty-state">
                        <div class="empty-icon">📂</div>
                        <div class="empty-title">No Activities Found</div>
                        <div class="empty-description">
                            Try adjusting your filters or check back later for new data
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="totalPages > 1" class="pagination-container">
                    <div class="pagination-info">
                        Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                        {{ Math.min(currentPage * itemsPerPage, filteredActivities.length) }}
                        of {{ filteredActivities.length }} entries
                        <span class="per-page-info">({{ itemsPerPage }} per page)</span>
                    </div>
                    <div class="pagination-controls">
                        <button class="page-btn prev-btn" @click="currentPage = 1" :disabled="currentPage === 1">
                            Previous
                        </button>
                        <button v-for="page in visiblePageNumbers" :key="page" class="page-btn page-number"
                            :class="{ 'active': page === currentPage }" @click="currentPage = page">
                            {{ page }}
                        </button>
                        <button class="page-btn next-btn" @click="currentPage++" :disabled="currentPage === totalPages">
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'FamsIotATGDashboard',

    data() {
        return {
            // Popup states
            showEventsBreakdown: false,
            eventsByDevice: [],
            totalEventsInRange: 0,
            eventsBreakdownTitle: '',
            currentPeriodLabel: '',

            showVolumeBreakdown: false,
            volumeByDevice: [],
            volumeEventCount: 0,

            showDeviceList: false,
            deviceListWithStats: [],

            selectedActivity: null,

            // Loader states
            showLoaderOption: true,
            isLoading: false,
            loaderMessage: 'Loading...',
            loaderTimeout: null,
            minLoaderTime: 1500,
            loaderStartTime: null,

            // Auto-refresh settings
            autoRefreshEnabled: false,
            refreshInterval: 30000,
            refreshTimer: null,
            nextRefreshTime: null,

            // Connection and loading states
            connectionStatus: 'Live',
            loading: false,
            lastUpdate: null,
            errorCount: 0,

            // API Configuration
            apiConfig: {
                baseURL: process.env.VUE_APP_API_URL || '',
                accountId: localStorage.getItem('userAccountId'),
                refreshInterval: 15000,
            },

            // Pagination
            currentPage: 1,
            itemsPerPage: 15,

            // Data
            activities: [],
            rawApiData: [],
            uniqueDevices: [],
            uniqueStores: [],

            // Statistics
            stats: {
                totalDevices: 0,
                totalActivities: 0,
                totalVolume: 0,
                totalStores: 0,
                processedCount: 0,
                unprocessedCount: 0,
                processingRate: 95,
                eventsTrend: 12.5,
                avgVolume: 0
            },

            // Filters
            filters: {
                startDateTime: this.getDefaultStartDate(),
                endDateTime: this.getDefaultEndDate(),
                activityTypes: [],
                deviceIds: [],
                statuses: [],
                specificDate: ''
            },
            selectedDateRange: 'last3days',
            dateRangeOptions: [
                { value: 'last3days', label: 'Last 3 Days' },
                { value: 'today', label: 'Today' },
                { value: 'yesterday', label: 'Yesterday' },
                { value: '2daysago', label: '2 Days Ago' }
            ],
            filtersVisible: true,
            statsVisible: true,

            // Dropdown states for multi-select
            dropdownStates: {
                activityType: false,
                device: false,
                status: false,
                columns: false
            },

            // activityTypeOptions and statusOptions are now dynamic computed properties

            columnSearchFilters: {},

            // ATG-specific columns
            availableColumns: [
                { key: 'timestamp', label: 'Date', visible: true },
                { key: 'deviceId', label: 'Device ID', visible: true },
                { key: 'storeName', label: 'Store Name', visible: true },
                { key: 'type', label: 'Activity Type', visible: true },
                { key: 'telemetryType', label: 'Telemetry Type', visible: true },
                { key: 'message', label: 'Message', visible: true },
                { key: 'telemetryData', label: 'Telemetry Data', visible: true },
                { key: 'tankVolumes', label: 'Tank Volumes', visible: true },
                { key: 'status', label: 'Status', visible: true },
                { key: 'storeID', label: 'Store ID', visible: false },
                { key: 'accountID', label: 'Account ID', visible: false },
                { key: 'accountName', label: 'Account Name', visible: false },
                { key: 'recordTypeID', label: 'Record Type ID', visible: false },
                { key: 'iotDeviceID', label: 'IOT Device ID', visible: false },
                { key: 'iotDeviceDescription', label: 'Device Description', visible: false },
                { key: 'iotDeviceTypeID', label: 'Device Type ID', visible: false },
                { key: 'iotDeviceTankID', label: 'Tank ID', visible: false },
                { key: 'storeMacAddress', label: 'MAC Address', visible: false },
                { key: 'storeIPAddress', label: 'IP Address', visible: false },
            ],

            selectedColumns: [
                'timestamp', 'deviceId', 'storeName', 'type',
                'telemetryType', 'message', 'tankVolumes', 'status'
            ],

            pollingInterval: null
        };
    },

    computed: {
        filteredActivities() {
            let filtered = [...this.activities];

            if (this.filters.startDateTime) {
                const startDate = new Date(this.filters.startDateTime);
                filtered = filtered.filter(a => new Date(a.timestamp || a.createDate) >= startDate);
            }
            if (this.filters.endDateTime) {
                const endDate = new Date(this.filters.endDateTime);
                filtered = filtered.filter(a => new Date(a.timestamp || a.createDate) <= endDate);
            }
            if (this.filters.activityTypes.length > 0) {
                filtered = filtered.filter(a => this.filters.activityTypes.includes(a.atgEventType));
            }
            if (this.filters.deviceIds.length > 0) {
                filtered = filtered.filter(a => this.filters.deviceIds.includes(a.storeName));
            }
            if (this.filters.statuses.length > 0) {
                filtered = filtered.filter(a => this.filters.statuses.includes(a.status));
            }

            // Column-level search filters
            if (Object.keys(this.columnSearchFilters).length > 0) {
                filtered = filtered.filter(activity => {
                    for (const key in this.columnSearchFilters) {
                        const sv = this.columnSearchFilters[key];
                        if (!sv || !sv.trim()) continue;
                        const search = sv.toLowerCase();

                        let val = '';
                        if (key === 'timestamp') {
                            val = this.formatDateTime(activity.timestamp || activity.createDate) || '';
                        } else if (key === 'deviceId') {
                            val = ((activity.deviceId || '') + ' ' + (activity.deviceName || ''));
                        } else if (key === 'telemetryType') {
                            val = String(activity.telemetryType != null ? activity.telemetryType : '');
                        } else {
                            val = String(activity[key] != null ? activity[key] : '');
                        }

                        if (!val.toLowerCase().includes(search)) return false;
                    }
                    return true;
                });
            }

            return filtered;
        },

        totalPages() {
            return Math.ceil(this.filteredActivities.length / this.itemsPerPage);
        },

        paginatedActivities() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            return this.filteredActivities.slice(start, start + this.itemsPerPage);
        },

        hasActiveColumnSearches() {
            return Object.keys(this.columnSearchFilters).some(k =>
                this.columnSearchFilters[k] && this.columnSearchFilters[k].trim() !== ''
            );
        },

        visiblePageNumbers() {
            const maxVisible = 5;
            let start = Math.max(1, this.currentPage - 2);
            let end = Math.min(this.totalPages, start + maxVisible - 1);
            if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
            const pages = [];
            for (let i = start; i <= end; i++) pages.push(i);
            return pages;
        },

        visibleColumns() {
            return this.availableColumns.filter(col => this.selectedColumns.includes(col.key));
        },

        activeDateRangeLabel() {
            if (this.filters.specificDate)
                return `Showing: ${this.filters.specificDate} (00:00 – 23:59)`;
            const opt = this.dateRangeOptions.find(o => o.value === this.selectedDateRange);
            return opt ? opt.label : '';
        },

        // Dynamic — only ATG event types present in the loaded data
        activityTypeOptions() {
            const types = [...new Set(this.activities.map(a => a.atgEventType).filter(Boolean))];
            return types.sort().map(t => ({ value: t, label: t }));
        },

        // Dynamic — only statuses present in the loaded data
        statusOptions() {
            const statuses = [...new Set(this.activities.map(a => a.status).filter(Boolean))];
            return statuses.sort().map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }));
        }
    },

    watch: {
        filteredActivities: {
            handler() {
                this.updateStats();
            },
            deep: false
        }
    },

    methods: {
        getStatsDateRangeLabel() {
            if (this.filters.startDateTime && this.filters.endDateTime) {
                const start = new Date(this.filters.startDateTime);
                const end = new Date(this.filters.endDateTime);
                const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                if (daysDiff === 3) return 'Total Events (Last 3 Days)';
                if (daysDiff === 1) return 'Total Events (Today)';
                if (daysDiff === 7) return 'Total Events (Last Week)';
                return `Total Events (${daysDiff} Days)`;
            }
            return 'Total Events';
        },

        handleColumnSearch() {
            this.currentPage = 1;
        },

        clearColumnSearches() {
            this.columnSearchFilters = {};
            this.currentPage = 1;
        },

        getDefaultStartDate() {
            const date = new Date();
            date.setDate(date.getDate() - 3);
            date.setHours(0, 0, 0, 0);
            return this.formatDateForInput(date);
        },

        getDefaultEndDate() {
            const date = new Date();
            date.setHours(23, 59, 59, 999);
            return this.formatDateForInput(date);
        },

        formatDateForInput(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const mins = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day}T${hours}:${mins}`;
        },

        selectDateRange(rangeValue) {
            this.selectedDateRange = rangeValue;
            this.filters.specificDate = '';
            const now = new Date();
            const offsets = { last3days: -3, today: 0, yesterday: -1, '2daysago': -2 };
            const offset = offsets[rangeValue];

            const start = new Date(now);
            start.setDate(start.getDate() + (rangeValue === 'last3days' ? -3 : offset));
            start.setHours(0, 0, 0, 0);

            const end = new Date(now);
            if (rangeValue !== 'last3days') end.setDate(end.getDate() + offset);
            end.setHours(23, 59, 59, 999);

            this.filters.startDateTime = this.formatDateForInput(start);
            this.filters.endDateTime = this.formatDateForInput(end);
            this.applyFilters();
        },

        openEventsBreakdown() {
            const eventsToAnalyze = this.filteredActivities;
            this.totalEventsInRange = eventsToAnalyze.length;

            if (this.filters.startDateTime || this.filters.endDateTime) {
                this.eventsBreakdownTitle = `${this.totalEventsInRange} Events in Selected Range`;
                const s = this.filters.startDateTime ? new Date(this.filters.startDateTime).toLocaleDateString() : 'Start';
                const e = this.filters.endDateTime ? new Date(this.filters.endDateTime).toLocaleDateString() : 'Now';
                this.currentPeriodLabel = `${s} - ${e}`;
            } else {
                this.eventsBreakdownTitle = `${this.totalEventsInRange} Events Today`;
                this.currentPeriodLabel = new Date().toLocaleDateString();
            }

            const deviceEventsMap = {};
            eventsToAnalyze.forEach(activity => {
                const deviceId = activity.deviceId;
                if (!deviceEventsMap[deviceId]) {
                    deviceEventsMap[deviceId] = {
                        deviceId, alias: activity.deviceName,
                        totalEvents: 0, eventTypes: {}, events: [], expanded: false,
                        firstEvent: activity.timestamp || activity.createDate,
                        lastEvent: activity.timestamp || activity.createDate,
                        hourCounts: {}
                    };
                }
                deviceEventsMap[deviceId].totalEvents++;
                deviceEventsMap[deviceId].events.push(activity);
                const et = activity.type;
                if (!deviceEventsMap[deviceId].eventTypes[et]) deviceEventsMap[deviceId].eventTypes[et] = 0;
                deviceEventsMap[deviceId].eventTypes[et]++;
                const eventTime = new Date(activity.timestamp || activity.createDate);
                if (eventTime < new Date(deviceEventsMap[deviceId].firstEvent)) deviceEventsMap[deviceId].firstEvent = activity.timestamp || activity.createDate;
                if (eventTime > new Date(deviceEventsMap[deviceId].lastEvent)) deviceEventsMap[deviceId].lastEvent = activity.timestamp || activity.createDate;
                const hour = eventTime.getHours();
                if (!deviceEventsMap[deviceId].hourCounts[hour]) deviceEventsMap[deviceId].hourCounts[hour] = 0;
                deviceEventsMap[deviceId].hourCounts[hour]++;
            });

            this.eventsByDevice = Object.values(deviceEventsMap).map(device => {
                const eventTypesArray = Object.entries(device.eventTypes)
                    .map(([type, count]) => ({ type, count }))
                    .sort((a, b) => b.count - a.count);
                let peakHour = 0, maxCount = 0;
                Object.entries(device.hourCounts).forEach(([hour, count]) => {
                    if (count > maxCount) { maxCount = count; peakHour = parseInt(hour); }
                });
                return {
                    ...device, eventTypes: eventTypesArray,
                    percentage: this.totalEventsInRange > 0 ? (device.totalEvents / this.totalEventsInRange) * 100 : 0,
                    peakHour: `${peakHour}:00 - ${peakHour + 1}:00 (${maxCount} events)`
                };
            }).sort((a, b) => b.totalEvents - a.totalEvents);

            this.showEventsBreakdown = true;
        },

        openVolumeBreakdown() {
            const deviceVolumeMap = {};
            let totalVolumeEvents = 0;

            this.filteredActivities.forEach(activity => {
                if (activity.volume && activity.volume > 0) {
                    totalVolumeEvents++;
                    const deviceId = activity.deviceId;
                    if (!deviceVolumeMap[deviceId]) {
                        deviceVolumeMap[deviceId] = {
                            deviceId, alias: activity.deviceName,
                            totalVolume: 0, eventCount: 0, volumes: [], maxVolume: 0
                        };
                    }
                    deviceVolumeMap[deviceId].totalVolume += activity.volume;
                    deviceVolumeMap[deviceId].eventCount++;
                    deviceVolumeMap[deviceId].volumes.push(activity.volume);
                    deviceVolumeMap[deviceId].maxVolume = Math.max(deviceVolumeMap[deviceId].maxVolume, activity.volume);
                }
            });

            const totalVolume = this.stats.totalVolume || 0;
            this.volumeByDevice = Object.values(deviceVolumeMap)
                .map(device => ({
                    ...device,
                    avgVolume: device.eventCount > 0 ? device.totalVolume / device.eventCount : 0,
                    percentage: totalVolume > 0 ? (device.totalVolume / totalVolume) * 100 : 0
                }))
                .sort((a, b) => b.totalVolume - a.totalVolume);

            this.volumeEventCount = totalVolumeEvents;
            this.showVolumeBreakdown = true;
        },

        openDeviceList() {
            this.deviceListWithStats = this.uniqueDevices.map(deviceId => {
                const deviceActivities = this.filteredActivities.filter(a => a.deviceId === deviceId);
                const deviceName = deviceActivities.length > 0 ? deviceActivities[0].deviceName : '';
                const totalVolume = deviceActivities.reduce((sum, a) => sum + (parseFloat(a.volume) || 0), 0);
                const lastActivity = deviceActivities[0];
                return {
                    id: deviceId, alias: deviceName,
                    eventCount: deviceActivities.length, totalVolume,
                    lastStatus: lastActivity ? lastActivity.status : 'unknown',
                    lastActivity: lastActivity ? this.formatDateTime(lastActivity.timestamp || lastActivity.createDate) : 'N/A'
                };
            }).sort((a, b) => b.eventCount - a.eventCount);

            this.showDeviceList = true;
        },

        showActivityDetails(activity) {
            this.selectedActivity = activity;
        },

        startLoader(message = 'Loading...') {
            if (this.showLoaderOption) {
                this.loaderMessage = message;
                this.isLoading = true;
                this.loaderStartTime = Date.now();
                if (this.loaderTimeout) clearTimeout(this.loaderTimeout);
                this.loaderTimeout = setTimeout(() => {
                    console.warn('Loader timeout reached, hiding loader');
                    this.stopLoader(true);
                }, 30000);
            }
        },

        stopLoader(forceStop = false) {
            const elapsed = this.loaderStartTime ? Date.now() - this.loaderStartTime : 0;
            const remaining = Math.max(0, this.minLoaderTime - elapsed);

            const clear = () => {
                this.isLoading = false;
                this.loaderMessage = 'Loading...';
                this.loaderStartTime = null;
                if (this.loaderTimeout) { clearTimeout(this.loaderTimeout); this.loaderTimeout = null; }
            };

            if (forceStop || remaining === 0) {
                clear();
            } else {
                setTimeout(clear, remaining);
            }
        },

        handleAutoRefreshToggle() {
            if (this.autoRefreshEnabled) {
                this.startAutoRefresh();
                this.showNotification('Success', `Auto-refresh enabled (every ${this.formatRefreshInterval()})`, 'success');
            } else {
                this.stopAutoRefresh();
                this.showNotification('Info', 'Auto-refresh disabled - use manual refresh button', 'primary');
            }
        },

        handleRefreshIntervalChange() {
            if (this.autoRefreshEnabled) {
                this.stopAutoRefresh();
                this.startAutoRefresh();
                this.showNotification('Success', `Refresh interval changed to ${this.formatRefreshInterval()}`, 'success');
            }
        },

        startAutoRefresh() {
            this.stopAutoRefresh();
            if (this.autoRefreshEnabled) {
                this.nextRefreshTime = Date.now() + this.refreshInterval;
                this.refreshTimer = setInterval(() => {
                    this.loadIotATGData();
                    this.nextRefreshTime = Date.now() + this.refreshInterval;
                }, this.refreshInterval);
            }
        },

        stopAutoRefresh() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer);
                this.refreshTimer = null;
                this.nextRefreshTime = null;
            }
        },

        formatRefreshInterval() {
            const seconds = this.refreshInterval / 1000;
            if (seconds < 60) return `${seconds}s`;
            const minutes = seconds / 60;
            return minutes < 60 ? `${minutes}m` : `${minutes / 60}h`;
        },

        savePreferences() {
            try {
                localStorage.setItem('fams-iot-atg-preferences', JSON.stringify({
                    statsVisible: this.statsVisible,
                    filtersVisible: this.filtersVisible,
                    autoRefreshEnabled: this.autoRefreshEnabled,
                    refreshInterval: this.refreshInterval,
                    showLoaderOption: this.showLoaderOption,
                    minLoaderTime: this.minLoaderTime,
                    itemsPerPage: this.itemsPerPage,
                    selectedColumns: this.selectedColumns
                }));
            } catch (e) { console.log('Could not save preferences:', e); }
        },

        loadPreferences() {
            try {
                const saved = localStorage.getItem('fams-iot-atg-preferences');
                if (saved) {
                    const p = JSON.parse(saved);
                    this.statsVisible = p.statsVisible !== undefined ? p.statsVisible : true;
                    this.filtersVisible = p.filtersVisible !== undefined ? p.filtersVisible : true;
                    this.autoRefreshEnabled = p.autoRefreshEnabled || false;
                    this.refreshInterval = p.refreshInterval || 30000;
                    this.showLoaderOption = p.showLoaderOption !== undefined ? p.showLoaderOption : true;
                    this.minLoaderTime = p.minLoaderTime || 1500;
                    this.itemsPerPage = p.itemsPerPage || 15;
                    if (p.selectedColumns) this.selectedColumns = p.selectedColumns;
                }
            } catch (e) { console.log('Could not load preferences:', e); }
        },

        toggleDropdown(type) {
            Object.keys(this.dropdownStates).forEach(key => {
                if (key !== type) this.dropdownStates[key] = false;
            });
            this.dropdownStates[type] = !this.dropdownStates[type];
        },

        toggleAllActivityTypes() {
            this.filters.activityTypes = this.filters.activityTypes.length === 0
                ? [...this.activityTypeOptions.map(o => o.value)]
                : [];
            this.applyFilters();
        },

        toggleAllDevices() {
            this.filters.deviceIds = this.filters.deviceIds.length === 0
                ? [...this.uniqueDevices]
                : [];
            this.applyFilters();
        },

        toggleAllStatuses() {
            this.filters.statuses = this.filters.statuses.length === 0
                ? [...this.statusOptions.map(o => o.value)]
                : [];
            this.applyFilters();
        },

        isColumnVisible(columnKey) {
            return this.selectedColumns.includes(columnKey);
        },

        toggleAllColumns() {
            this.selectedColumns = this.selectedColumns.length === this.availableColumns.length
                ? ['timestamp', 'deviceId', 'storeName', 'type', 'telemetryType', 'message', 'tankVolumes', 'status']
                : this.availableColumns.map(col => col.key);
            this.savePreferences();
        },

        handleColumnVisibilityChange() {
            this.savePreferences();
            this.showNotification('Success', 'Column visibility updated', 'success');
        },

        formatColumnLabel(key) {
            return key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, str => str.toUpperCase())
                .replace(/Id$/i, 'ID')
                .replace(/Iot/i, 'IOT')
                .replace(/Atg/i, 'ATG');
        },

        handleClickOutside(event) {
            const containers = this.$el.querySelectorAll('.multi-select-container');
            let clickedInside = false;
            containers.forEach(c => { if (c.contains(event.target)) clickedInside = true; });
            if (!clickedInside) {
                Object.keys(this.dropdownStates).forEach(key => { this.dropdownStates[key] = false; });
            }
        },

        // -------------------------------------------------------------------------
        // ATG-specific: map ATGEventType string to a short badge type
        // -------------------------------------------------------------------------
        getActivityType(atgEventType) {
            if (!atgEventType) return 'system';
            const s = String(atgEventType).toLowerCase();
            if (s.includes('error')) return 'error';
            if (s.includes('warn')) return 'warning';
            if (s.includes('alert')) return 'alert';
            if (s.includes('atg')) return 'atg';
            return 'system';
        },

        // Extract per-tank volumes from telemetryData JSON.
        // Handles the exact API shape: { TelementryData: [{ prodName, vol, ... }] }
        extractTanksFromTelemetry(telemetryData) {
            if (!telemetryData) return [];
            try {
                const parsed = typeof telemetryData === 'string' ? JSON.parse(telemetryData) : telemetryData;
                const tanks =
                    parsed.TelementryData ||
                    parsed.telementryData ||
                    parsed.TelemetryData  ||
                    parsed.telemetryData  ||
                    [];
                if (!Array.isArray(tanks) || tanks.length === 0) return [];
                return tanks.map(t => ({
                    id:        t.id,
                    name:      (t.prodName && t.prodName.trim()) ? t.prodName.trim() : ('Tank ' + t.id),
                    vol:       parseFloat(t.vol)      || 0,
                    volTC:     parseFloat(t.volTC)    || 0,
                    mm:        parseFloat(t.mm)       || 0,
                    temp:      parseFloat(t.temp)     || 0,
                    waterVol:  parseFloat(t.waterVol) || 0,
                    weight:    parseFloat(t.weight)   || 0,
                    status:    t.status,
                    stateInfo: t.stateInfo || '',
                    msg:       t.Msg || ''
                }));
            } catch (e) {
                console.warn('extractTanksFromTelemetry parse error:', e);
                return [];
            }
        },

        // Total volume across all tanks (used for stats)
        extractVolumeFromMessage(message, telemetryData) {
            const tanks = this.extractTanksFromTelemetry(telemetryData);
            if (tanks.length > 0) {
                return tanks.reduce((sum, t) => sum + t.vol, 0);
            }
            if (!message) return 0;
            const m = message.match(/(\d+(?:\.\d+)?)\s*L/i) || message.match(/(\d+(?:\.\d+)?)\s*litre/i);
            return m ? parseFloat(m[1]) : 0;
        },

        determineStatus(telemetryType, atgEventType) {
            if (atgEventType && String(atgEventType).toLowerCase().includes('error')) return 'error';
            if (telemetryType === 205) return 'error';
            if (telemetryType === 124) return 'processed';
            if (telemetryType === 125) return 'pending';
            return 'processed';
        },

        // -------------------------------------------------------------------------
        // Data loading — uses the ATG endpoint
        // -------------------------------------------------------------------------
        async loadIotATGData() {
            var self = this;
            try {
                self.startLoader('Loading ATG data...');
                var start = self.formatDateTime(self.filters.startDateTime);
                var end = self.formatDateTime(self.filters.endDateTime);

                self.loading = true;
                self.connectionStatus = 'Loading...';

                var onSuccess = function (response) {
                    if (response.data) {
                        self.rawApiData = response.data;
                        self.processApiData(response.data);
                        self.lastUpdate = new Date();
                        self.connectionStatus = 'Live';
                        self.errorCount = 0;

                        if (!self.pollingInterval) {
                            const dateRangeText = self.filters.startDateTime && self.filters.endDateTime ? ' (Last 3 days)' : '';
                            self.showNotification('Success', `Loaded ${self.filteredActivities.length} ATG records${dateRangeText}`, 'success');
                        }
                    }
                    self.stopLoader();
                };

                var onFinally = function () {
                    self.loading = false;
                    self.stopLoader();
                };

                // ATG-specific URL
                var url = `VwIotdataNotificationComprehensiveActivity/GetIOTDataATG?accountId=${self.apiConfig.accountId}&startDate=${start}&endDate=${end}`;

                self.$ajaxGet(self, url, onSuccess, onFinally);

            } catch (error) {
                console.error('Error loading IOT ATG data:', error);
                this.errorCount++;
                this.connectionStatus = 'Connection Error';
                if (this.errorCount >= 3) {
                    this.showNotification('Error', 'Failed to load ATG data. Retrying...', 'danger');
                }
                this.loading = false;
                this.stopLoader();
            }
        },

        // -------------------------------------------------------------------------
        // Map raw API response fields (from the provided ATG field mapping) to
        // internal activity objects
        // -------------------------------------------------------------------------
        processApiData(apiData) {
            console.log('Processing ATG API data:', apiData);

            this.activities = apiData.map(item => {
                // Normalise every field using the same dual-case pattern from the snippet
                const ID = item.ID != null ? item.ID : item.id;
                const AccountID = item.AccountID != null ? item.AccountID : item.accountID;
                const IOTDeviceID = item.IOTDeviceID || item.iotDeviceID || '';
                const CreateDate = item.CreateDate || item.createDate || null;
                const Time = item.Time || item.time || '';
                const RecordTypeID = item.RecordTypeID != null ? item.RecordTypeID : item.recordTypeID;
                const EventTime = item.EventTime || item.eventTime || null;
                const TypeID = item.TypeID != null ? item.TypeID : item.typeID;
                const Account_ID = item.Account_ID != null ? item.Account_ID : item.account_ID;
                const AccountName = item.AccountName || item.accountName || '';
                const Store_ID = item.Store_ID != null ? item.Store_ID : (item.store_ID != null ? item.store_ID : null);
                const StoreName = item.StoreName || item.storeName || '';
                const StoreMacAddress = item.StoreMacAddress || item.storeMacAddress || '';
                const StoreIPAddress = item.StoreIPAddress || item.storeIPAddress || '';
                const IOTDevice_ID = item.IOTDevice_ID != null ? item.IOTDevice_ID : item.iotDevice_ID;
                const IOTDevice_DeviceID = item.IOTDevice_DeviceID || item.iotDevice_DeviceID || '';
                const IOTDevice_DeviceName = item.IOTDevice_DeviceName || item.iotDevice_DeviceName || '';
                const IOTDevice_Name = item.IOTDevice_Name || item.iotDevice_Name || '';
                const IOTDevice_Description = item.IOTDevice_Description || item.iotDevice_Description || '';
                const IOTDevice_TypeID = item.IOTDevice_TypeID != null ? item.IOTDevice_TypeID : item.iotDevice_TypeID;
                const IOTDevice_TankID = item.IOTDevice_TankID != null ? item.IOTDevice_TankID : item.iotDevice_TankID;
                const Telementry_Data = item.telementry_Data || item.Telementry_Data || item.telementryData || item.TelementryData || item.TelemetryData || item.telemetryData || '';
                const Telemetry_Time = item.Telemetry_Time || item.telemetry_Time || '';
                const Telemetry_RecordTypeID = item.Telemetry_RecordTypeID != null ? item.Telemetry_RecordTypeID : item.telemetry_RecordTypeID;
                const Telemetry_Type = item.Telemetry_Type != null ? item.Telemetry_Type : item.telemetry_Type;
                const Telemetry_Message = item.Telemetry_Message || item.telemetry_Message || '';
                const ATGEventType = item.ATGEventType || item.atgEventType || item.ATGeventType || '';

                return {
                    id: ID,
                    createDate: CreateDate,
                    timestamp: Time || (CreateDate ? new Date(CreateDate).getTime() : null),

                    // Device
                    deviceId: IOTDevice_DeviceID || IOTDeviceID,
                    deviceName: IOTDevice_DeviceName || IOTDevice_Name,
                    iotDeviceID: IOTDeviceID,
                    iotDeviceDescription: IOTDevice_Description,
                    iotDeviceTypeID: IOTDevice_TypeID,
                    iotDeviceTankID: IOTDevice_TankID,

                    // Store / Account
                    storeID: Store_ID,
                    storeName: StoreName,
                    storeMacAddress: StoreMacAddress,
                    storeIPAddress: StoreIPAddress,
                    accountID: AccountID,
                    accountName: AccountName,

                    // Record / Type
                    recordTypeID: RecordTypeID,
                    typeID: TypeID,

                    // ATG-specific
                    atgEventType: ATGEventType,
                    telemetryType: Telemetry_Type,
                    telemetryData: Telementry_Data,
                    message: Telemetry_Message || 'No message',
                    telemetryTime: Telemetry_Time,
                    telemetryRecordTypeID: Telemetry_RecordTypeID,

                    // Derived
                    tanks: this.extractTanksFromTelemetry(Telementry_Data),
                    type: this.getActivityType(ATGEventType),
                    volume: this.extractVolumeFromMessage(Telemetry_Message, Telementry_Data),
                    status: this.determineStatus(Telemetry_Type, ATGEventType),
                    eventTime: EventTime
                };
            });

            console.log('Total ATG records processed:', this.activities.length);

            // Dynamically add any extra columns found in the data
            if (this.activities.length > 0) {
                Object.keys(this.activities[0]).forEach(key => {
                    if (!this.availableColumns.find(col => col.key === key)) {
                        this.availableColumns.push({ key, label: this.formatColumnLabel(key), visible: false });
                    }
                });
            }

            // Sort newest first
            this.activities.sort((a, b) => new Date(b.createDate) - new Date(a.createDate));

            this.updateFilterOptions();
            this.updateStats();
        },

        updateFilterOptions() {
            this.uniqueDevices = [...new Set(this.activities.map(a => a.storeName).filter(Boolean))];
            this.uniqueStores = [...new Set(this.activities.map(a => a.storeID).filter(Boolean))];
        },

        updateStats() {
            const data = this.filteredActivities;
            this.stats.totalDevices = [...new Set(data.map(a => a.deviceId).filter(Boolean))].length;
            this.stats.totalStores = [...new Set(data.map(a => a.storeID).filter(Boolean))].length;
            this.stats.totalActivities = data.length;

            // Trend: compare first half vs second half of the date range
            if (data.length > 0) {
                const sorted = [...data].sort((a, b) => new Date(a.createDate || a.timestamp) - new Date(b.createDate || b.timestamp));
                const mid = Math.floor(sorted.length / 2);
                const firstHalf = sorted.slice(0, mid);
                const secondHalf = sorted.slice(mid);
                this.stats.eventsTrend = firstHalf.length > 0
                    ? ((secondHalf.length - firstHalf.length) / firstHalf.length) * 100
                    : 0;
            } else {
                this.stats.eventsTrend = 0;
            }

            const volumeSum = data.reduce((sum, a) => sum + (parseFloat(a.volume) || 0), 0);
            this.stats.totalVolume = volumeSum;
            const withVol = data.filter(a => a.volume && a.volume > 0);
            this.stats.avgVolume = withVol.length > 0 ? volumeSum / withVol.length : 0;

            this.stats.processedCount = data.filter(a => a.status === 'processed').length;
            this.stats.unprocessedCount = data.filter(a => a.status === 'pending').length;
            this.stats.processingRate = data.length > 0 ? (this.stats.processedCount / data.length) * 100 : 0;
        },

        formatDateTime(date) {
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const h = String(d.getHours()).padStart(2, '0');
            const m = String(d.getMinutes()).padStart(2, '0');
            const s = String(d.getSeconds()).padStart(2, '0');
            return `${year}/${month}/${day} ${h}:${m}:${s}`;
        },

        getRelativeTime(date) {
            const seconds = Math.floor((new Date() - new Date(date)) / 1000);
            if (seconds < 60) return 'Just now';
            if (seconds < 3600) return Math.floor(seconds / 60) + ' minutes ago';
            if (seconds < 86400) return Math.floor(seconds / 3600) + ' hours ago';
            if (seconds < 604800) return Math.floor(seconds / 86400) + ' days ago';
            return new Date(date).toLocaleDateString();
        },

        showNotification(title, text, type = 'primary') {
            if (this.$vs && this.$vs.notify) {
                this.$vs.notify({ title, text, color: type, position: 'top-right' });
            } else {
                console.log(`${title}: ${text}`);
            }
        },

        applyFilters() {
            this.startLoader('Applying filters...');
            this.currentPage = 1;
            this.updateStats();
            setTimeout(() => {
                this.showNotification('Success', 'Filters applied', 'success');
                this.stopLoader();
            }, 500);
        },

        resetFilters() {
            this.startLoader('Resetting filters...');
            this.columnSearchFilters = {};
            this.filters = {
                startDateTime: this.getDefaultStartDate(),
                endDateTime: this.getDefaultEndDate(),
                activityTypes: [],
                deviceIds: [],
                statuses: [],
                specificDate: ''
            };
            this.selectedDateRange = 'last3days';
            Object.keys(this.dropdownStates).forEach(key => {
                if (key !== 'columns') this.dropdownStates[key] = false;
            });
            this.currentPage = 1;
            this.updateStats();
            setTimeout(() => {
                this.showNotification('Success', 'Filters reset to last 3 days', 'success');
                this.stopLoader();
            }, 300);
        },

        async refreshData() {
            this.startLoader('Refreshing data...');
            this.showNotification('Info', 'Refreshing data...', 'primary');
            try {
                await this.loadIotATGData();
            } catch (error) {
                console.error('Error refreshing data:', error);
                this.stopLoader();
            }
        },

        exportData() {
            this.startLoader('Exporting data...');
            this.showNotification('Info', 'Exporting data...', 'primary');
            setTimeout(() => {
                const headers = this.visibleColumns.map(col => col.label);
                const csvRows = this.filteredActivities.map(activity => {
                    return this.visibleColumns.map(col => {
                        let value = '';
                        switch (col.key) {
                            case 'timestamp': value = this.formatDateTime(activity.timestamp || activity.createDate); break;
                            case 'volume': value = activity.volume || ''; break;
                            case 'message': value = `"${activity.message || ''}"`; break;
                            case 'telemetryData': value = `"${activity.telemetryData || ''}"`; break;
                            default: value = activity[col.key] || '';
                        }
                        return value;
                    }).join(',');
                });
                const csvContent = [headers.join(','), ...csvRows].join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `fams_iot_atg_data_${new Date().getTime()}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
                this.showNotification('Success', 'Data exported successfully', 'success');
                this.stopLoader();
            }, 1000);
        },

        handleRowsChange() {
            this.currentPage = 1;
            this.showNotification('Success', `Showing ${this.itemsPerPage} rows per page`, 'success');
        },

        openInNewWindow() {
            this.startLoader('Opening in new window...');
            const columnHeaders = this.visibleColumns.map(col => `<th>${col.label}</th>`).join('');
            const tableRows = this.filteredActivities.map(activity => {
                const cells = this.visibleColumns.map(col => {
                    let cellContent = '';
                    switch (col.key) {
                        case 'timestamp': cellContent = this.formatDateTime(activity.timestamp || activity.createDate); break;
                        case 'deviceId': cellContent = `${activity.deviceId}<br><small style="color:#999;">${activity.deviceName || ''}</small>`; break;
                        case 'type': cellContent = `<span class="badge badge-${activity.type}">${activity.type}</span>`; break;
                        case 'status': cellContent = `<span class="status-${activity.status}">${activity.status}</span>`; break;
                        case 'telemetryType': cellContent = activity.telemetryType != null ? activity.telemetryType : '-'; break;
                        default: cellContent = activity[col.key] || '-';
                    }
                    return `<td>${cellContent}</td>`;
                }).join('');
                return `<tr>${cells}</tr>`;
            }).join('');

            const popupHTML = `<!DOCTYPE html><html><head><title>FAMS IOT ATG Feed</title>
        <style>
          body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;margin:0;padding:20px;background:#f5f5f5;}
          .header{background:linear-gradient(135deg,#D2691E 0%,#FF8C00 100%);color:white;padding:20px;border-radius:8px;margin-bottom:20px;}
          h1{margin:0;font-size:24px;}.info{margin-top:10px;font-size:14px;opacity:.9;}
          .container{background:white;border-radius:8px;padding:20px;box-shadow:0 2px 8px rgba(0,0,0,.1);overflow-x:auto;}
          table{width:100%;border-collapse:collapse;}
          th,td{padding:10px;text-align:left;border-bottom:1px solid #e0e0e0;}
          th{background:linear-gradient(135deg,#D2691E 0%,#FF8C00 100%);color:white;font-weight:600;position:sticky;top:0;}
          tr:hover{background:#f8f9fa;}
          .badge{display:inline-block;padding:3px 8px;border-radius:4px;font-size:11px;font-weight:600;}
          .badge-atg{background:#D2691E;color:white;}.badge-alert{background:#C0392B;color:white;}
          .badge-warning{background:#E67E22;color:white;}.badge-error{background:#E74C3C;color:white;}
          .badge-system{background:#34495E;color:white;}
          .status-processed{color:#27AE60;}.status-pending{color:#F39C12;}.status-error{color:#E74C3C;}
        
/* ---- Tank row layout ---- */
.shared-cell {
    border-left: 3px solid transparent;
    vertical-align: middle;
}
.tank-first-row td { border-top: 2px solid #e8d5c4 !important; }
.tank-first-row .shared-cell { border-left: 3px solid #D2691E; }
.tank-last-row td { border-bottom: 2px solid #e8d5c4 !important; }
.tank-subsequent-row td { background: #fffaf7; }
.tank-cell { white-space: nowrap; padding: 6px 12px !important; }
.tank-cell-last { border-bottom: 2px solid #e8d5c4 !important; }
.tank-row-display { display: flex; align-items: center; gap: 8px; }
.tank-number-badge {
    display: inline-block;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    white-space: nowrap;
    min-width: 48px;
    text-align: center;
}
.tank-vol-amount { font-weight: 700; font-size: 0.88rem; color: #2C3E50; }

/* ---- Tank Detail Table (popup) ---- */
.tank-table-wrapper { overflow-x: auto; margin-top: 6px; }
.tank-detail-table { width: 100%; border-collapse: collapse; font-size: 0.82rem; }
.tank-detail-table th {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white; padding: 6px 10px; text-align: left; font-weight: 600; white-space: nowrap;
}
.tank-detail-table td { padding: 6px 10px; border-bottom: 1px solid #e8e8e8; color: #333; }
.tank-detail-table tbody tr:hover { background: #fff8f3; }
.tank-detail-table .vol-cell { font-weight: 700; color: #D2691E; }
.tank-total-row td { background: #fff3e8; border-top: 2px solid #D2691E; }
.state-badge { display: inline-block; padding: 2px 7px; border-radius: 10px; font-size: 0.75rem; font-weight: 600; }
.state-done { background: #e8f5e9; color: #2e7d32; }
.state-other { background: #fff3e0; color: #e65100; }

</style></head><body>
        <div class="header"><h1>FAMS IOT ATG Feed</h1>
          <div class="info">
            <div>Generated: ${new Date().toLocaleString()}</div>
            <div>Total Records: ${this.filteredActivities.length}</div>
            <div>Last Update: ${this.lastUpdate ? new Date(this.lastUpdate).toLocaleString() : 'N/A'}</div>
          </div></div>
        <div class="container"><table>
          <thead><tr>${columnHeaders}</tr></thead>
          <tbody>${tableRows}</tbody>
        </table></div></body></html>`;

            const win = window.open('', 'FAMS_IOT_ATG', 'width=1400,height=800,resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=yes');
            if (win) {
                win.document.write(popupHTML);
                win.document.close();
                this.showNotification('Success', 'Opened in new window', 'success');
            } else {
                this.showNotification('Warning', 'Please allow pop-ups for this site', 'warning');
            }
            this.stopLoader();
        },

        simulateRealTimeUpdate() {
            setInterval(() => {
                if (Math.random() > 0.95) {
                    this.connectionStatus = 'Updating...';
                    setTimeout(() => { this.connectionStatus = 'Live'; }, 1000);
                }
            }, 10000);
        }
    },

    mounted() {
        this.loadPreferences();
        if (!this.filters.startDateTime) this.filters.startDateTime = this.getDefaultStartDate();
        if (!this.filters.endDateTime) this.filters.endDateTime = this.getDefaultEndDate();

        this.loadIotATGData();

        if (this.autoRefreshEnabled) this.startAutoRefresh();

        this.simulateRealTimeUpdate();
        document.addEventListener('click', this.handleClickOutside);

        setTimeout(() => {
            const refreshStatus = this.autoRefreshEnabled
                ? `Auto-refresh enabled (every ${this.formatRefreshInterval()})`
                : 'Auto-refresh is disabled by default';
            this.showNotification('Welcome!', `FAMS ATG Data Dashboard - Showing last 3 days - ${refreshStatus}`, 'success');
        }, 500);
    },

    beforeDestroy() {
        this.stopAutoRefresh();
        if (this.loaderTimeout) { clearTimeout(this.loaderTimeout); this.loaderTimeout = null; }
        document.removeEventListener('click', this.handleClickOutside);
    }
}
</script>

<style>
/* ============================================================
   All styles are identical to IOTDataWarnings.vue
   ============================================================ */
:root {
    --fams-primary: #2C3E50;
    --fams-secondary: #3498DB;
    --fams-accent: #E74C3C;
    --fams-success: #27AE60;
    --fams-warning: #F39C12;
    --fams-danger: #E74C3C;
    --fams-info: #3498DB;
    --fams-dark: #34495E;
    --fams-light: #ECF0F1;

    --autumn-primary: #D2691E;
    --autumn-secondary: #CD853F;
    --autumn-accent: #FF8C00;
    --autumn-warm: #FFA500;
    --autumn-deep: #8B4513;
    --autumn-light: #FFE4B5;
    --autumn-gold: #DAA520;
    --autumn-rust: #B22222;

    --gradient-primary: linear-gradient(135deg, var(--autumn-primary) 0%, var(--autumn-accent) 100%);
    --gradient-success: linear-gradient(135deg, var(--fams-success) 0%, #2ECC71 100%);
    --gradient-warning: linear-gradient(135deg, var(--fams-warning) 0%, #F1C40F 100%);
    --gradient-danger: linear-gradient(135deg, var(--fams-danger) 0%, #C0392B 100%);

    --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, .05);
    --shadow-base: 0 2px 8px rgba(0, 0, 0, .1);
    --shadow-md: 0 4px 16px rgba(0, 0, 0, .15);
    --shadow-lg: 0 10px 30px rgba(0, 0, 0, .2);
}

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #e2e8f0;
    height: 100vh;
    overflow: hidden;
    width: 100%;
    margin: 0;
    padding: 0;
}

#app {
    height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    max-width: none;
}

.message-cell {
    min-width: 250px;
    max-width: 500px;
    white-space: normal;
    word-wrap: break-word;
    word-break: break-word;
    line-height: 1.4;
    padding: 4px 0;
}

/* Header */
.header-toolbar {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    padding: 16px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 20px;
}

.logo-section {
    display: flex;
    align-items: center;
    gap: 12px;
}

.logo-icon {
    width: 40px;
    height: 40px;
    background: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: var(--autumn-primary);
}

.dashboard-title {
    color: white;
    font-size: 1.5rem;
    font-weight: 600;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.loader-controls {
    display: flex;
    align-items: center;
    gap: 15px;
}

.loader-toggle {
    display: flex;
    align-items: center;
}

.toggle-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
}

.toggle-checkbox {
    width: 18px;
    height: 18px;
    margin-right: 8px;
    cursor: pointer;
    accent-color: white;
}

.toggle-text {
    color: white;
    font-size: 0.875rem;
    font-weight: 500;
}

.loader-speed {
    display: flex;
    align-items: center;
}

.speed-label {
    display: flex;
    align-items: center;
    gap: 8px;
}

.speed-text {
    color: white;
    font-size: 0.875rem;
    font-weight: 500;
}

.speed-select {
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, .3);
    background: rgba(255, 255, 255, .2);
    color: white;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all .3s ease;
}

.speed-select:hover {
    background: rgba(255, 255, 255, .3);
}

.speed-select:focus {
    outline: none;
    border-color: white;
}

.speed-select option {
    background: #D2691E;
    color: white;
}

.auto-refresh-toggle {
    display: flex;
    align-items: center;
    padding-left: 15px;
    border-left: 1px solid rgba(255, 255, 255, .3);
}

.refresh-interval {
    display: flex;
    align-items: center;
}

.interval-label {
    display: flex;
    align-items: center;
    gap: 8px;
}

.interval-text {
    color: white;
    font-size: 0.875rem;
    font-weight: 500;
}

.interval-select {
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, .3);
    background: rgba(255, 255, 255, .2);
    color: white;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all .3s ease;
}

.interval-select:hover {
    background: rgba(255, 255, 255, .3);
}

.interval-select:focus {
    outline: none;
    border-color: white;
}

.interval-select option {
    background: #D2691E;
    color: white;
}

.live-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, .2);
    padding: 8px 16px;
    border-radius: 20px;
}

.live-dot {
    width: 8px;
    height: 8px;
    background: #4CAF50;
    border-radius: 50%;
    animation: pulse 2s infinite;
}

.live-dot.error {
    background: #f44336;
    animation: pulse-error 2s infinite;
}

.live-dot.auto-refresh {
    background: #4CAF50;
    animation: pulse 1s infinite;
}

/* Global loader */
.global-loader-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, .7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    backdrop-filter: blur(2px);
    animation: fadeIn .3s ease;
}

.global-loader-content {
    background: white;
    border-radius: 16px;
    padding: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, .3);
    animation: slideUp .3s ease;
}

.global-loader {
    width: 60px;
    height: 60px;
    border: 4px solid #FFE4B5;
    border-top: 4px solid #D2691E;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.loader-text {
    color: #34495E;
    font-size: 1rem;
    font-weight: 500;
    margin-top: 10px;
}

/* Keyframes */
@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideUp {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(76, 175, 80, .7);
    }

    70% {
        box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
    }
}

@keyframes pulse-error {
    0% {
        box-shadow: 0 0 0 0 rgba(244, 67, 54, .7);
    }

    70% {
        box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
    }
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes water-drop {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-3px);
    }
}

/* Main content */
.main-content {
    flex: 1;
    overflow: visible;
    padding: 20px;
    width: 100%;
    background: #e2e8f0;
    position: relative;
}

/* Stats section */
.stats-section {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15), 0 1px 2px rgba(0, 0, 0, .1);
    overflow: visible;
    width: 100%;
    border: 1px solid #cbd5e1;
}

.stats-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.stats-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #34495E;
    margin: 0;
}

.toggle-stats-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .1);
}

.toggle-stats-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(210, 105, 30, .3);
}

.stats-wrapper {
    padding: 16px 20px;
}

.stats-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.stat-card {
    background: #f8fafc;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    transition: all .3s ease;
    border: 1px solid #e2e8f0;
    position: relative;
    z-index: 1;
    overflow: visible;
}

.stat-card * {
    position: relative;
    z-index: 2;
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, .12);
    background: #fff;
}

.stat-label {
    color: #2c3e50 !important;
    font-size: 0.875rem;
    margin-bottom: 8px;
    font-weight: 600;
    position: relative;
    z-index: 2;
    display: block;
    line-height: 1.2;
}

.stat-value {
    font-size: 1.75rem;
    color: #2c3e50 !important;
    font-weight: bold;
    background: linear-gradient(135deg, #FFFFFF 0%, #FF8C00 100%);
    -webkit-background-clip: text;
    background-clip: text;
}

.stat-change {
    font-size: 0.75rem;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.stat-change .stat-icon {
    font-size: 0.875rem;
}

.stat-change.positive {
    color: var(--fams-success);
}

.stat-change.negative {
    color: var(--fams-danger);
}

/* Search section */
.search-section {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15), 0 1px 2px rgba(0, 0, 0, .1);
    overflow: visible;
    width: 100%;
    border: 1px solid #cbd5e1;
}

.search-section:has(.multi-select-dropdown) {
    z-index: 100;
    position: relative;
}

.filter-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.filter-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #34495E;
    margin: 0;
}

.toggle-filter-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .1);
}

.toggle-filter-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(210, 105, 30, .3);
}

.toggle-icon {
    font-size: 1rem;
}

.search-controls-wrapper {
    padding: 16px 20px;
    position: relative;
    overflow: visible;
}

.search-controls {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
    overflow: visible;
    position: relative;
}

.search-group {
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: visible;
}

.search-label {
    font-size: 0.875rem;
    color: #34495E;
    margin-bottom: 4px;
    font-weight: 500;
}

.filter-actions {
    display: flex;
    gap: 12px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
    position: relative;
    z-index: 1;
}

.apply-filters-btn {
    padding: 10px 24px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all .3s ease;
}

.apply-filters-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(210, 105, 30, .3);
}

.reset-filters-btn {
    padding: 10px 24px;
    background: white;
    color: #34495E;
    border: 2px solid #FFE4B5;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all .3s ease;
}

.reset-filters-btn:hover {
    background: #FFE4B5;
}

/* Multi-select */
.multi-select-container {
    position: relative;
    overflow: visible;
}

.multi-select-display {
    padding: 10px 12px;
    border: 1px solid #94a3b8;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all .3s ease;
    min-height: 44px;
}

.multi-select-display:hover {
    border-color: #D2691E;
    background: #fff8f3;
}

.multi-select-display .placeholder {
    color: #999;
}

.multi-select-display .selected-items {
    color: #495057;
    font-weight: 500;
}

.dropdown-arrow {
    color: #999;
    font-size: 0.75rem;
    transition: transform .3s ease;
}

.multi-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 2px solid #D2691E;
    border-radius: 8px;
    margin-top: 4px;
    max-height: 250px;
    overflow-y: auto;
    z-index: 9999;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
}

.select-all-option {
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    background: #f8f9fa;
}

.dropdown-options {
    max-height: 200px;
    overflow-y: auto;
}

.checkbox-option {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    transition: background .2s ease;
}

.checkbox-option:hover {
    background: #fff3e0;
}

.checkbox-option input[type="checkbox"] {
    margin-right: 8px;
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #D2691E;
}

.checkbox-option span {
    font-size: 0.875rem;
    color: #495057;
    user-select: none;
}

.multi-badge {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 0.75rem;
    font-weight: 600;
    margin-left: 4px;
}

.dropdown-options::-webkit-scrollbar {
    width: 6px;
}

.dropdown-options::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.dropdown-options::-webkit-scrollbar-thumb {
    background: #D2691E;
    border-radius: 3px;
}

.dropdown-options::-webkit-scrollbar-thumb:hover {
    background: #8B4513;
}

/* Slide transition */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all .3s ease;
    overflow: visible;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

/* Grid */
.grid-container {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15), 0 1px 2px rgba(0, 0, 0, .1);
    overflow: hidden;
    width: 100%;
    border: 1px solid #cbd5e1;
}

.grid-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.grid-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #34495E;
}

.grid-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.rows-selector {
    display: flex;
    align-items: center;
    gap: 8px;
}

.rows-label {
    font-size: 0.875rem;
    color: #6c757d;
    font-weight: 500;
}

.rows-select {
    padding: 6px 12px;
    border: 1px solid #94a3b8;
    border-radius: 6px;
    background: white;
    color: #475569;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all .3s ease;
    min-width: 100px;
}

.rows-select:hover {
    border-color: #D2691E;
}

.rows-select:focus {
    outline: none;
    border-color: #D2691E;
    box-shadow: 0 0 0 3px rgba(210, 105, 30, .1);
}

.grid-actions {
    display: flex;
    gap: 8px;
}

.action-button {
    padding: 8px 14px;
    background: white;
    border: 1px solid #94a3b8;
    border-radius: 6px;
    color: #475569;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all .3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
}

.action-button:hover {
    background: #f8fafc;
    border-color: #D2691E;
    color: #D2691E;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, .08);
}

.action-button.popout-btn:hover {
    background: #e3f2fd;
    border-color: #2196F3;
    color: #2196F3;
}

.action-button.refresh-btn:hover {
    background: #e8f5e9;
    border-color: #4CAF50;
    color: #4CAF50;
}

.action-button.export-btn:hover {
    background: #fff3e0;
    border-color: #FF9800;
    color: #FF9800;
}

.btn-icon {
    font-size: 1rem;
}

.action-button:disabled {
    opacity: .5;
    cursor: not-allowed;
}

/* Table */
.table-container {
    overflow: auto;
    position: relative;
    max-height: 500px;
    width: 100%;
    background: #fff;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table thead {
    position: sticky;
    top: 0;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    z-index: 10;
}

.data-table th {
    padding: 12px 16px;
    text-align: left;
    color: white;
    font-weight: 600;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: .5px;
    white-space: nowrap;
}

.data-table tbody tr {
    border-bottom: 1px solid #e2e8f0;
    transition: all .2s ease;
    background: #fff;
}

.data-table tbody tr:hover {
    background: rgba(210, 105, 30, .1);
    cursor: pointer;
}

.data-table tbody tr:nth-child(even) {
    background: #f8fafc;
}

.data-table tbody tr:nth-child(even):hover {
    background: #f1f5f9;
}

.data-table td {
    padding: 12px 16px;
    font-size: 0.875rem;
    color: var(--fams-dark);
}

/* Badges */
.activity-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
}

.badge-atg {
    background: linear-gradient(135deg, #D2691E, #FF8C00);
    color: white;
}

.badge-alert {
    background: linear-gradient(135deg, #C0392B, #A93226);
    color: white;
}

.badge-warning {
    background: linear-gradient(135deg, #E67E22, #D35400);
    color: white;
}

.badge-error {
    background: linear-gradient(135deg, #E74C3C, #C0392B);
    color: white;
}

.badge-system {
    background: linear-gradient(135deg, #34495E, #2C3E50);
    color: white;
}

/* Keep the full original badge set for any mapped types */
.badge-dispense {
    background: linear-gradient(135deg, #3498DB, #2980B9);
    color: white;
}

.badge-complete {
    background: linear-gradient(135deg, #27AE60, #229954);
    color: white;
}

.badge-action {
    background: linear-gradient(135deg, #9B59B6, #8E44AD);
    color: white;
}

.badge-stop {
    background: linear-gradient(135deg, #E74C3C, #C0392B);
    color: white;
}

.badge-limit {
    background: linear-gradient(135deg, #E67E22, #D35400);
    color: white;
}

.badge-auth {
    background: linear-gradient(135deg, #3498DB, #2E86AB);
    color: white;
}

.badge-select {
    background: linear-gradient(135deg, #95A5A6, #7F8C8D);
    color: white;
}

.badge-odometer {
    background: linear-gradient(135deg, #16A085, #138D75);
    color: white;
}

.badge-driver {
    background: linear-gradient(135deg, #8E44AD, #6C3483);
    color: white;
}

/* Status */
.status-indicator {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
}

.status-processed {
    background: rgba(39, 174, 96, .1);
    color: var(--fams-success);
}

.status-pending {
    background: rgba(243, 156, 18, .1);
    color: var(--fams-warning);
}

.status-error {
    background: rgba(231, 76, 60, .1);
    color: var(--fams-danger);
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
}

/* Device / volume display */
.device-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.device-id {
    font-weight: 600;
    color: var(--fams-dark);
}

.device-alias {
    font-size: 0.75rem;
    color: var(--fams-secondary);
}

.volume-display {
    font-weight: 600;
    color: var(--autumn-primary);
}

/* Empty state */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: var(--fams-dark);
    background: #f8fafc;
    border-radius: 8px;
    margin: 20px;
    border: 1px solid #e2e8f0;
}

.empty-icon {
    font-size: 4rem;
    color: var(--autumn-light);
    margin-bottom: 16px;
}

.empty-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 8px;
}

.empty-description {
    color: #6c757d;
    text-align: center;
}

/* Loading overlay */
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(241, 245, 249, .95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
}

.loader {
    width: 48px;
    height: 48px;
    border: 4px solid var(--autumn-light);
    border-top: 4px solid var(--autumn-primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

code {
    background: #f5f5f5;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
}

/* Pagination */
.pagination-container {
    padding: 16px 20px;
    border-top: 1px solid #cbd5e1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f1f5f9;
}

.pagination-info {
    color: #6c757d;
    font-size: 0.875rem;
}

.per-page-info {
    color: #D2691E;
    font-weight: 500;
}

.pagination-controls {
    display: flex;
    gap: 4px;
}

.page-btn {
    padding: 6px 12px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    color: #495057;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all .2s ease;
    font-weight: 500;
    min-width: 40px;
}

.page-btn:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #D2691E;
    color: #D2691E;
}

.page-btn.active {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-color: #D2691E;
}

.page-btn:disabled {
    opacity: .5;
    cursor: not-allowed;
}

.prev-btn,
.next-btn {
    min-width: 70px;
}

/* Popup */
.simple-popup {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, .5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}

.popup-content {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    overflow: auto;
    box-shadow: 0 4px 20px rgba(0, 0, 0, .3);
}

.popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e0e0e0;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-radius: 8px 8px 0 0;
}

.popup-header h3 {
    margin: 0;
    font-size: 1.25rem;
}

.close-btn {
    background: none;
    border: none;
    color: white;
    font-size: 28px;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background .2s;
}

.close-btn:hover {
    background: rgba(255, 255, 255, .2);
}

.popup-body {
    padding: 20px;
}

.detail-row {
    margin-bottom: 12px;
    font-size: 0.9rem;
}

.detail-row strong {
    color: #34495E;
    display: inline-block;
    min-width: 120px;
}

.message-text {
    margin-top: 8px;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 4px;
    line-height: 1.5;
    word-wrap: break-word;
}

/* Clickable stat */
.clickable-stat {
    cursor: pointer;
    transition: all .3s ease;
}

.clickable-stat:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, .15);
    background: #fff !important;
    border-color: #D2691E;
}

.clickable-stat::after {
    content: "📋";
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 0.875rem;
    opacity: 0;
    transition: opacity .3s ease;
}

.clickable-stat:hover::after {
    opacity: .7;
}

/* Device list popup */
.device-list-container {
    max-height: 400px;
    overflow-y: auto;
}

.device-list-item {
    padding: 12px;
    border-bottom: 1px solid #e2e8f0;
    transition: background .2s ease;
}

.device-list-item:hover {
    background: #f8fafc;
}

.device-list-item:last-child {
    border-bottom: none;
}

.device-main-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.device-main-info strong {
    color: #D2691E;
    font-size: 1rem;
}

.device-stats {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.device-stat-badge {
    display: inline-block;
    padding: 3px 8px;
    background: #f1f5f9;
    border-radius: 4px;
    font-size: 0.75rem;
    color: #475569;
    border: 1px solid #e2e8f0;
}

.device-stat-badge.volume {
    background: #e0f2fe;
    color: #0369a1;
    border-color: #7dd3fc;
}

.device-stat-badge.status-processed {
    background: #dcfce7;
    color: #14532d;
    border-color: #86efac;
}

.device-stat-badge.status-pending {
    background: #fef3c7;
    color: #78350f;
    border-color: #fde047;
}

.device-stat-badge.status-error {
    background: #fee2e2;
    color: #7f1d1d;
    border-color: #fca5a5;
}

.no-devices {
    text-align: center;
    padding: 40px 20px;
    color: #94a3b8;
    font-style: italic;
}

.device-list-container::-webkit-scrollbar {
    width: 6px;
}

.device-list-container::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.device-list-container::-webkit-scrollbar-thumb {
    background: #D2691E;
    border-radius: 3px;
}

.device-list-container::-webkit-scrollbar-thumb:hover {
    background: #8B4513;
}

/* Volume popup */
.volume-summary {
    background: #f8fafc;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    border: 1px solid #e2e8f0;
}

.summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.summary-item:last-child {
    margin-bottom: 0;
}

.summary-label {
    color: #64748b;
    font-size: 0.875rem;
}

.summary-value {
    font-weight: 600;
    color: #D2691E;
    font-size: 1rem;
}

.volume-list-container {
    max-height: 400px;
    overflow-y: auto;
}

.volume-list-container h4 {
    margin: 0 0 12px 0;
    color: #334155;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: .5px;
}

.volume-item {
    padding: 12px;
    border-bottom: 1px solid #e2e8f0;
    transition: background .2s ease;
}

.volume-item:hover {
    background: #f8fafc;
}

.volume-item:last-child {
    border-bottom: none;
}

.volume-device-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.device-name {
    display: flex;
    align-items: center;
    gap: 8px;
}

.device-name strong {
    color: #1e293b;
    font-size: 0.875rem;
}

.volume-amount {
    display: flex;
    align-items: center;
    gap: 6px;
}

.volume-value {
    font-weight: 700;
    color: #D2691E;
    font-size: 1rem;
}

.volume-percentage {
    color: #64748b;
    font-size: 0.75rem;
}

.volume-bar-container {
    width: 100%;
    height: 20px;
    background: #f1f5f9;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 8px;
}

.volume-bar {
    height: 100%;
    background: linear-gradient(90deg, #3498DB 0%, #2196F3 100%);
    transition: width .3s ease;
    border-radius: 10px;
}

.volume-details {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.detail-badge {
    display: inline-block;
    padding: 3px 8px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: 0.7rem;
    color: #475569;
}

.no-volume {
    text-align: center;
    padding: 40px 20px;
    color: #94a3b8;
    font-style: italic;
}

.volume-list-container::-webkit-scrollbar {
    width: 6px;
}

.volume-list-container::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.volume-list-container::-webkit-scrollbar-thumb {
    background: #3498DB;
    border-radius: 3px;
}

.volume-list-container::-webkit-scrollbar-thumb:hover {
    background: #2196F3;
}

/* Events popup */
.events-summary {
    background: #f8fafc;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    border: 1px solid #e2e8f0;
}

.events-list-container {
    max-height: 450px;
    overflow-y: auto;
}

.events-list-container h4 {
    margin: 0 0 12px 0;
    color: #334155;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: .5px;
}

.events-device-item {
    margin-bottom: 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    transition: all .3s ease;
}

.events-device-item:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, .1);
}

.device-header {
    padding: 12px;
    background: white;
    cursor: pointer;
    transition: background .2s ease;
}

.device-header:hover {
    background: #f8fafc;
}

.device-info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.device-name-section {
    display: flex;
    align-items: center;
    gap: 8px;
}

.device-name-section strong {
    color: #D2691E;
    font-size: 0.9rem;
}

.device-count {
    display: flex;
    align-items: center;
    gap: 8px;
}

.event-count-badge {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
}

.expand-icon {
    color: #64748b;
    font-size: 0.75rem;
    transition: transform .2s ease;
}

.device-progress-bar {
    width: 100%;
    height: 6px;
    background: #f1f5f9;
    border-radius: 3px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #D2691E 0%, #FF8C00 100%);
    transition: width .3s ease;
}

.device-events-detail {
    padding: 12px;
    background: #fafbfc;
    border-top: 1px solid #e2e8f0;
    animation: slideDown .3s ease;
}

.event-type-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 8px;
    margin-bottom: 12px;
}

.event-type-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.event-type-item .activity-badge {
    font-size: 0.65rem;
    padding: 2px 6px;
}

.event-count {
    font-weight: 600;
    color: #334155;
    font-size: 0.875rem;
}

.device-timeline {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 8px;
}

.timeline-item {
    display: flex;
    flex-direction: column;
    padding: 6px;
    background: white;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
}

.timeline-label {
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
}

.timeline-value {
    color: #1e293b;
    font-size: 0.75rem;
    font-weight: 500;
}

.no-events {
    text-align: center;
    padding: 40px 20px;
    color: #94a3b8;
    font-style: italic;
}

.events-list-container::-webkit-scrollbar {
    width: 6px;
}

.events-list-container::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.events-list-container::-webkit-scrollbar-thumb {
    background: #D2691E;
    border-radius: 3px;
}

.events-list-container::-webkit-scrollbar-thumb:hover {
    background: #FF8C00;
}

/* Column search row */
.search-row {
    background: #fff3e0 !important;
}

.search-row th {
    background: transparent !important;
    padding: 6px !important;
    border-right: 1px solid #ffcc80 !important;
}

.column-search-input {
    width: 100%;
    padding: 5px 8px;
    border: 1px solid #ffcc80;
    border-radius: 4px;
    font-size: 0.75rem;
    background: white;
    color: #333;
    transition: all .2s;
    font-weight: normal;
    letter-spacing: normal;
    text-transform: none;
    box-sizing: border-box;
}

.column-search-input:focus {
    outline: none;
    border-color: #ff9800;
    box-shadow: 0 0 0 2px rgba(255, 152, 0, .2);
}

.column-search-input::placeholder {
    color: #999;
    font-style: italic;
}

.clear-column-searches-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .2s;
    display: flex;
    align-items: center;
    gap: 6px;
}

.clear-column-searches-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(255, 82, 82, .4);
}

/* Date filter group */
.date-filter-group {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 8px 6px;
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, .05);
}

.date-range-showing {
    font-size: 0.75rem;
    color: #64748b;
    text-transform: none;
    letter-spacing: 0;
    display: inline-block;
    margin-left: 6px;
    font-weight: normal;
}

.date-quick-buttons {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
}

.date-quick-btn {
    padding: 5px 10px;
    border: 1px solid #cbd5e1;
    background: white;
    border-radius: 5px;
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
    cursor: pointer;
    transition: all .2s ease;
    white-space: nowrap;
}

.date-quick-btn:hover {
    border-color: #D2691E;
    color: #D2691E;
    background: #fff9f5;
}

.date-quick-btn.active {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-color: #D2691E;
    box-shadow: 0 2px 8px rgba(210, 105, 30, .2);
}

/* Responsive */
@media (max-width: 768px) {
    .main-content {
        padding: 12px;
    }

    .header-content {
        padding: 0 12px;
    }

    .stats-container {
        grid-template-columns: 1fr 1fr;
    }

    .search-controls {
        grid-template-columns: 1fr;
    }

    .data-table {
        font-size: 0.75rem;
    }

    .data-table th,
    .data-table td {
        padding: 8px;
    }

    .activity-badge {
        font-size: 0.625rem;
        padding: 2px 6px;
    }

    .header-controls {
        flex-wrap: wrap;
    }

    .grid-controls {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }

    .grid-actions {
        justify-content: space-between;
    }

    .message-cell {
        min-width: 150px;
        max-width: 250px;
        font-size: 0.75rem;
    }

    .stats-header,
    .filter-header,
    .grid-header,
    .stats-wrapper,
    .search-controls-wrapper,
    .pagination-container {
        padding-left: 12px;
        padding-right: 12px;
    }
}

@media (max-width: 480px) {
    .header-content {
        flex-direction: column;
        gap: 15px;
    }

    .loader-controls {
        flex-wrap: wrap;
        gap: 10px;
    }

    .stats-container {
        grid-template-columns: 1fr;
    }

    .pagination-container {
        flex-direction: column;
        gap: 15px;
        align-items: stretch;
    }

    .pagination-controls {
        justify-content: center;
    }

    .page-btn {
        min-width: 30px;
        padding: 4px 8px;
        font-size: 0.75rem;
    }

    .message-cell {
        min-width: 100px;
        max-width: 200px;
    }
}

@media (min-width: 1400px) {
    .message-cell {
        max-width: 600px;
    }
}

@media (min-width: 2000px) {

    .header-content,
    .main-content {
        max-width: 1920px;
        margin: 0 auto;
    }
}
</style>